import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type {
	CreateFlowRequest,
	Flow,
	ListFlowsParams,
	PaginatedResponse,
	UpdateFlowRequest,
} from '../types/api';
import { folderTreeKeys } from './useFolderTree';

/**
 * Query keys for React Query caching
 */
export const flowKeys = {
	all: ['flows'] as const,
	lists: () => [...flowKeys.all, 'list'] as const,
	list: (params: ListFlowsParams) => [...flowKeys.lists(), { params }] as const,
	details: () => [...flowKeys.all, 'detail'] as const,
	detail: (id: string) => [...flowKeys.details(), id] as const,
};

/**
 * Hook to fetch flows with caching and background updates
 */
export function useFlows(params: ListFlowsParams = {}) {
	return useQuery({
		queryKey: flowKeys.list(params),
		queryFn: () => api.flows.getFlows(params),
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
	});
}

/**
 * Hook to fetch a single flow
 */
export function useFlow(id: string) {
	return useQuery({
		queryKey: flowKeys.detail(id),
		queryFn: () => api.flows.getFlow(id),
		enabled: !!id,
		staleTime: 5 * 60 * 1000,
	});
}

/**
 * Hook to create a new flow with optimistic updates
 */
export function useCreateFlow() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateFlowRequest) => api.flows.createFlow(data),
		onSuccess: newFlow => {
			// Invalidate and refetch flows list
			queryClient.invalidateQueries({ queryKey: flowKeys.lists() });

			// Add the new flow to the cache
			queryClient.setQueryData(flowKeys.detail(newFlow.id), newFlow);

			// IMPORTANT: Invalidate folder cache to update flowCount
			queryClient.invalidateQueries({ queryKey: ['folders'] });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });
		},
	});
}

/**
 * Hook to update a flow with optimistic updates
 */
export function useUpdateFlow() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateFlowRequest }) =>
			api.flows.updateFlow(id, data),
		onMutate: async ({ id, data }) => {
			// Cancel outgoing refetches
			await queryClient.cancelQueries({ queryKey: flowKeys.detail(id) });

			// Snapshot the previous value
			const previousFlow = queryClient.getQueryData<Flow>(flowKeys.detail(id));

			// Optimistically update the cache
			if (previousFlow) {
				queryClient.setQueryData<Flow>(flowKeys.detail(id), {
					...previousFlow,
					...data,
					updatedAt: new Date().toISOString(),
				});
			}

			return { previousFlow };
		},
		onError: (err, variables, context) => {
			// Rollback on error
			if (context?.previousFlow) {
				queryClient.setQueryData(flowKeys.detail(variables.id), context.previousFlow);
			}
		},
		onSettled: (data, error, variables) => {
			// Always refetch after success or error
			queryClient.invalidateQueries({ queryKey: flowKeys.detail(variables.id) });
			queryClient.invalidateQueries({ queryKey: flowKeys.lists() });

			// IMPORTANT: Invalidate folder cache to update flowCount
			queryClient.invalidateQueries({ queryKey: ['folders'] });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });
		},
	});
}

/**
 * Hook to delete a flow with optimistic updates
 */
export function useDeleteFlow() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => api.flows.deleteFlow(id),
		onMutate: async id => {
			// Cancel outgoing refetches
			await queryClient.cancelQueries({ queryKey: flowKeys.lists() });

			// Remove from cache
			queryClient.removeQueries({ queryKey: flowKeys.detail(id) });

			// Optimistically update lists
			queryClient.setQueriesData<PaginatedResponse<Flow>>(
				{ queryKey: flowKeys.lists() },
				old => {
					if (!old) return old;
					return {
						...old,
						data: old.data.filter(flow => flow.id !== id),
						total: old.total - 1,
					};
				}
			);
		},
		onError: () => {
			// Refetch on error to restore correct state
			queryClient.invalidateQueries({ queryKey: flowKeys.lists() });
		},
		onSettled: () => {
			// Always refetch lists
			queryClient.invalidateQueries({ queryKey: flowKeys.lists() });

			// IMPORTANT: Invalidate folder cache to update flowCount
			queryClient.invalidateQueries({ queryKey: ['folders'] });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });
		},
	});
}

/**
 * Hook to clone a flow
 */
export function useCloneFlow() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, name }: { id: string; name?: string }) => api.flows.cloneFlow(id, name),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: flowKeys.lists() });

			// IMPORTANT: Invalidate folder cache to update flowCount
			queryClient.invalidateQueries({ queryKey: ['folders'] });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });
		},
	});
}

/**
 * Hook to publish a flow
 */
export function usePublishFlow() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => api.flows.publishFlow(id),
		onMutate: async id => {
			await queryClient.cancelQueries({ queryKey: flowKeys.detail(id) });

			const previousFlow = queryClient.getQueryData<Flow>(flowKeys.detail(id));

			if (previousFlow) {
				queryClient.setQueryData<Flow>(flowKeys.detail(id), {
					...previousFlow,
					status: 'published',
					updatedAt: new Date().toISOString(),
				});
			}

			return { previousFlow };
		},
		onError: (err, id, context) => {
			if (context?.previousFlow) {
				queryClient.setQueryData(flowKeys.detail(id), context.previousFlow);
			}
		},
		onSettled: (data, error, id) => {
			queryClient.invalidateQueries({ queryKey: flowKeys.detail(id) });
			queryClient.invalidateQueries({ queryKey: flowKeys.lists() });

			// IMPORTANT: Invalidate folder cache to update flowCount
			queryClient.invalidateQueries({ queryKey: ['folders'] });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });
		},
	});
}
